// A single body-level dialog stays intact when the product picker is re-rendered.
if (!window.__mmSizeGuideController) {
  window.__mmSizeGuideController = true;
  const chartChoices = new Map();
  const originalTexts = new WeakMap();
  const overflowHints = new WeakMap();
  let preferredUnit = 'in';
  let dialog;
  let activeHost;
  let returnTarget;
  let priorOverflow = '';
  let scrollLocked = false;
  let overflowObserver;
  let overflowFrame;

  const normalizeSize = (value) => String(value || '').trim().toUpperCase().replace(/\s+/g, '').replace(/^XXL$/, '2XL').replace(/^XXXL$/, '3XL').replace(/^XXXXL$/, '4XL').replace(/^XXXXXL$/, '5XL');
  const currentHost = () => Array.from(document.querySelectorAll('[data-mm-size-guide]')).find((host) => host.dataset.guideKey === activeHost?.dataset.guideKey) || activeHost;
  const currentChart = () => dialog?.querySelector('[data-mm-size-guide-chart]:not([hidden])');
  const originalText = (element) => {
    if (!originalTexts.has(element)) originalTexts.set(element, element.textContent);
    return originalTexts.get(element);
  };
  const canConvert = (chart) => {
    if (!chart || chart.querySelector('[data-mm-unit-toggle="disabled"]')) return false;
    const tables = Array.from(chart.querySelectorAll('table[data-mm-measurement-unit="in"]'));
    const labelsIntact = tables.length > 0 && tables.every((table) => {
      const headers = Array.from(table.querySelectorAll('thead th')).slice(1);
      return headers.length > 0 && headers.every((header) => {
        const label = header.querySelector('[data-mm-unit-label]');
        return label && /^IN(?:CH(?:ES)?)?$/i.test(originalText(label).trim());
      });
    });
    if (!labelsIntact) return false;
    const cells = Array.from(chart.querySelectorAll('table[data-mm-measurement-unit="in"] td')).filter((cell) => cell.textContent.trim());
    return cells.length > 0 && cells.every((cell) => {
      const inches = originalText(cell).trim();
      return cell.hasAttribute('data-mm-inches') && /^\d+(?:\.\d+)?$/.test(inches) && Number.isFinite(Number(inches));
    });
  };

  function updateOverflowHints() {
    overflowFrame = undefined;
    if (!dialog?.open) return;
    const mobile = window.innerWidth <= 749;
    dialog.querySelectorAll('.mm-size-guide__table-scroll').forEach((wrapper) => {
      const hint = overflowHints.get(wrapper);
      if (hint) hint.hidden = !mobile || Boolean(wrapper.closest('[hidden]')) ||
        wrapper.clientWidth === 0 || wrapper.scrollWidth <= wrapper.clientWidth + 1;
    });
  }

  function scheduleOverflowHints() {
    if (!dialog?.open || overflowFrame !== undefined) return;
    overflowFrame = requestAnimationFrame(updateOverflowHints);
  }

  function stopOverflowTracking() {
    overflowObserver?.disconnect();
    overflowObserver = undefined;
    window.removeEventListener('resize', scheduleOverflowHints);
    if (overflowFrame !== undefined) cancelAnimationFrame(overflowFrame);
    overflowFrame = undefined;
  }

  function startOverflowTracking() {
    stopOverflowTracking();
    window.addEventListener('resize', scheduleOverflowHints, { passive: true });
    if (typeof ResizeObserver === 'function') {
      overflowObserver = new ResizeObserver(scheduleOverflowHints);
      dialog.querySelectorAll('.mm-size-guide__table-scroll, .mm-size-guide__table-scroll > table').forEach((element) => overflowObserver.observe(element));
    }
    scheduleOverflowHints();
  }

  function updateUnits() {
    const chart = currentChart();
    const allowed = canConvert(chart);
    const unit = allowed ? preferredUnit : 'in';
    const group = dialog.querySelector('[data-mm-size-guide-units]');
    group.hidden = !allowed;
    group.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mmUnit === unit)));
    // Markers identify inch cells; their visible text is the source of truth.
    // This preserves merchant edits even if old numeric attributes remain.
    // Missing markers or mixed/range values safely disable the whole toggle.
    chart.querySelectorAll('table[data-mm-measurement-unit="in"]').forEach((table) => {
      table.querySelectorAll('[data-mm-inches]').forEach((cell) => {
        const text = originalText(cell);
        const inches = text.trim();
        if (unit === 'cm' && /^\d+(?:\.\d+)?$/.test(inches)) {
          cell.textContent = (Number(inches) * 2.54).toFixed(1).replace(/\.0$/, '');
        } else {
          cell.textContent = text;
        }
      });
      table.querySelectorAll('[data-mm-unit-label]').forEach((label) => {
        const text = originalText(label);
        label.textContent = unit === 'cm' ? 'CM' : text;
      });
    });
    scheduleOverflowHints();
  }

  function updateSelectedSize() {
    if (!dialog?.open) return;
    const host = currentHost();
    const picker = host?.closest('variant-picker');
    let selected = '';
    if (picker && !picker.classList.contains('no-preselect-active')) {
      const checked = picker.querySelector('[data-option-type="size"] input:checked');
      const select = Array.from(picker.querySelectorAll('select[name]')).find((element) => /size/i.test(element.name));
      selected = checked?.value || select?.value || '';
    }
    const size = normalizeSize(selected);
    let found = false;
    currentChart()?.querySelectorAll('tbody tr').forEach((row) => {
      const label = row.dataset.mmSize || row.querySelector('th[scope="row"]')?.textContent;
      const matches = Boolean(size && label && normalizeSize(label) === size);
      row.toggleAttribute('data-mm-selected-size', matches);
      if (matches) found = true;
    });
    const status = dialog.querySelector('[data-mm-size-guide-selection]');
    status.textContent = found ? `Your selected size: ${selected}` : '';
  }

  function collapseTeeStatement() {
    dialog.querySelectorAll('.mm-size-guide-content__notes').forEach((notes) => {
      // Only the original tee statement is optional reading; fit notes stay visible.
      if (!/^\s*\*{0,3}Official Statement on T-Shirts\*{0,3}/i.test(notes.textContent) || notes.closest('details')) return;
      const details = document.createElement('details');
      details.className = 'mm-size-guide__statement';
      const summary = document.createElement('summary');
      summary.textContent = 'About our T-shirts';
      notes.before(details);
      details.append(summary, notes);
    });
  }

  function chooseChart(handle) {
    const charts = Array.from(dialog.querySelectorAll('[data-mm-size-guide-chart]'));
    const chosen = charts.find((chart) => chart.dataset.mmSizeGuideChart === handle) || charts[0];
    charts.forEach((chart) => { chart.hidden = chart !== chosen; });
    const selectedHandle = chosen.dataset.mmSizeGuideChart;
    chartChoices.set(activeHost.dataset.productId, selectedHandle);
    const select = dialog.querySelector('[data-mm-size-guide-select]');
    if (select) select.value = selectedHandle;
    updateUnits();
    updateSelectedSize();
  }

  function restoreAfterClose() {
    stopOverflowTracking();
    if (scrollLocked) {
      document.documentElement.style.overflow = priorOverflow;
      scrollLocked = false;
    }
    const trigger = returnTarget?.isConnected ? returnTarget : currentHost()?.querySelector('[data-mm-size-guide-open]');
    trigger?.focus({ preventScroll: true });
  }

  function ensureDialog() {
    if (dialog?.isConnected) return dialog;
    dialog = document.createElement('dialog');
    if (typeof dialog.showModal !== 'function') return null;
    dialog.className = 'mm-size-guide-dialog';
    dialog.setAttribute('aria-labelledby', 'mm-size-guide-title');
    document.body.append(dialog);
    dialog.addEventListener('close', restoreAfterClose);
    dialog.addEventListener('cancel', () => { /* Native Escape closes the modal. */ });
    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-mm-size-guide-close]')) dialog.close();
      if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }
      const unitButton = event.target.closest('[data-mm-unit]');
      if (unitButton && canConvert(currentChart())) {
        preferredUnit = unitButton.dataset.mmUnit === 'cm' ? 'cm' : 'in';
        updateUnits();
      }
    });
    dialog.addEventListener('change', (event) => {
      if (event.target.matches('[data-mm-size-guide-select]')) chooseChart(event.target.value);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const candidates = Array.from(dialog.querySelectorAll('button, a[href], select, summary, [tabindex="0"]')).filter((element) => {
        if (element.disabled || element.closest('[hidden]')) return false;
        const closedDetails = element.closest('details:not([open])');
        return !closedDetails || element === closedDetails.querySelector('summary');
      });
      const first = candidates[0];
      const last = candidates[candidates.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    return dialog;
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mm-size-guide-open]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const host = trigger.closest('[data-mm-size-guide]');
    const template = host?.querySelector('template[data-mm-size-guide-template]');
    if (!template || !ensureDialog()) return;
    event.preventDefault();
    activeHost = host;
    returnTarget = trigger;
    stopOverflowTracking();
    dialog.replaceChildren(template.content.cloneNode(true));
    collapseTeeStatement();
    dialog.querySelectorAll('.mm-size-guide__content table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'mm-size-guide__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', `${table.closest('[data-mm-size-guide-chart]').getAttribute('aria-label')} measurements`);
      table.before(wrapper);
      wrapper.append(table);
      // Keep the hint outside the measured region so it cannot create overflow.
      const hint = document.createElement('p');
      hint.className = 'mm-size-guide__scroll-hint';
      hint.textContent = 'Swipe sideways to see the full chart →';
      hint.hidden = true;
      wrapper.before(hint);
      overflowHints.set(wrapper, hint);
    });
    chooseChart(chartChoices.get(host.dataset.productId));
    if (!scrollLocked) {
      priorOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      scrollLocked = true;
    }
    dialog.showModal();
    startOverflowTracking();
    updateSelectedSize();
    dialog.querySelector('[data-mm-size-guide-close]').focus({ preventScroll: true });
  });
  document.addEventListener('variant:update', () => requestAnimationFrame(updateSelectedSize));
  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-picker')) requestAnimationFrame(updateSelectedSize);
  });
}
